import { ClientConstraints } from 'hooks/webapi/useClientConstraints';
import { ClientOffer } from 'hooks/webapi/useClientOffer';
import {
  getConstraintsAmountValue,
  getConstraintsTermDefaultValue,
} from 'utils/defaultValues';
import { InstalmentCard } from 'components';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { PRODUCT_NUMBER_IL, PRODUCT_NUMBER_SPL } from 'utils/constants';
import { showNewConsTagAccordingtoBE } from 'pages/RepeatedLoan/components/ConsBanner';
import { useClient } from 'hooks/webapi';
import { useTranslation } from 'react-i18next';
import classes from 'classnames';
import NewLabelIcon from 'assets/icons/new-label.svg?react';
import Slider from 'components/Slider/Slider';
import styles from './Calculator.module.scss';
import Swiper from 'components/Swiper/Swiper';
import useBreakpoint from 'hooks/useBreakpoint';

export type Props = {
  onAmountChange: (value: number) => void;
  onTermChange: (value: number) => void;
  constraints: ClientConstraints;
  calculatorAmount?: number;
  calculatorTerm?: number;
  productCode?: 'VIVUS_SPL' | 'VIVUS_IL';
  onProductCodeChange?: (productCode: 'VIVUS_SPL' | 'VIVUS_IL') => void;
  offers?: Partial<ClientOffer>[];
  isLoadingOffer?: boolean;
  dataTest?: string;
  isConsEnabled?: boolean;
};

const Calculator = memo(function Calculator({
  onAmountChange,
  onTermChange,
  constraints,
  calculatorAmount,
  calculatorTerm,
  productCode = PRODUCT_NUMBER_SPL,
  onProductCodeChange,
  offers,
  isLoadingOffer,
  dataTest,
  isConsEnabled,
}: Props) {
  const { client } = useClient();
  const [isMobileSize, setIsMobileSize] = useState(false);
  const { t } = useTranslation('calculator');
  const { size } = useBreakpoint();

  const windowTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const checkSwiperVisibility = () => {
    windowTimeoutRef.current = setTimeout(() => {
      const nextIsMobileSize = window?.innerWidth < 1024;
      if (isMobileSize !== nextIsMobileSize) {
        setIsMobileSize(nextIsMobileSize);
      }
    }, 250);
  };

  useEffect(() => {
    window?.addEventListener('resize', checkSwiperVisibility);
    checkSwiperVisibility();
  }, []);

  const toggleProductType = useCallback(() => {
    if (onProductCodeChange) {
      onProductCodeChange(
        productCode === PRODUCT_NUMBER_SPL
          ? PRODUCT_NUMBER_IL
          : PRODUCT_NUMBER_SPL,
      );
    }
    checkSwiperVisibility();
  }, [onProductCodeChange, productCode]);

  const constraintsAmountDefaultValue = getConstraintsAmountValue(constraints);
  const constraintsTermDefaultValue =
    getConstraintsTermDefaultValue(constraints);

  return (
    <div data-test={dataTest} className={styles['calculator-wrapper']}>
      <Slider
        dataTest="amount-slider"
        min={constraints.amountInterval.min}
        max={constraints.amountInterval.max}
        step={constraints.amountInterval.step}
        value={
          calculatorAmount !== undefined
            ? calculatorAmount
            : constraintsAmountDefaultValue
        }
        onChange={onAmountChange}
        units={t('common:currency')}
        heading={t('amount_heading')}
        className={styles.slider}
      />
      {isMobileSize && (
        <Swiper
          key={size.name}
          dataTest="amount-swiper"
          min={constraints.amountInterval.min}
          max={constraints.amountInterval.max}
          step={constraints.amountInterval.step}
          defaultValue={
            calculatorAmount !== undefined
              ? calculatorAmount
              : constraintsAmountDefaultValue || 0
          }
          onChange={onAmountChange}
          snap={95}
          heading={t('amount_heading')}
          className={styles.swiper}
          unit={t('common:currency')}
        />
      )}

      {isConsEnabled && (
        <button
          data-test="product-toggle"
          className={styles.toggle}
          type="button"
          onClick={toggleProductType}
        >
          <span
            className={classes(
              styles['toggle-option'],
              productCode === PRODUCT_NUMBER_SPL && styles.active,
            )}
            data-test="calculator-mini"
          >
            {t('mini')}
          </span>
          <span
            className={classes(
              styles['toggle-option'],
              productCode === PRODUCT_NUMBER_IL && styles.active,
            )}
            data-test="calculator-cons"
          >
            {t('cons')}
          </span>
          {showNewConsTagAccordingtoBE(client) && (
            <NewLabelIcon
              className={styles['new-icon']}
              data-test="new-label-icon"
            />
          )}
        </button>
      )}

      {productCode === PRODUCT_NUMBER_SPL ? (
        <>
          <Slider
            dataTest="term-slider"
            min={constraints.termInterval.min}
            max={constraints.termInterval.max}
            step={constraints.termInterval.step}
            value={
              calculatorTerm !== undefined
                ? calculatorTerm
                : constraintsTermDefaultValue
            }
            onAfterChange={onTermChange}
            units={t('term_unit')}
            heading={t('term_heading')}
            className={styles.slider}
          />

          {isMobileSize && (
            <Swiper
              key={size.name}
              dataTest="term-swiper"
              min={constraints.termInterval.min}
              max={constraints.termInterval.max}
              step={constraints.termInterval.step}
              defaultValue={
                calculatorTerm !== undefined
                  ? calculatorTerm
                  : constraints.termInterval.defaultValue
              }
              onChange={onTermChange}
              snap={75}
              heading={t('term_heading')}
              className={styles.swiper}
              unit={t('common:days', { count: calculatorTerm || 0 })}
              unitPosition="bottom"
            />
          )}
        </>
      ) : (
        <>
          <span className={styles['instalment-heading']}>
            {t('instalment_heading')}
          </span>
          <div
            className={styles['instalment-cards']}
            data-test="calculator-instalment-card"
          >
            {constraints.instalments.map(({ term, amountFrom }) => {
              const termOffer = offers?.find((offer) => offer.term === term);

              // Don't show installment if client's credit limit doesn't allow it
              if (amountFrom > constraints.amountInterval.max) return null;

              return (
                <div
                  key={`term-${term}`}
                  className={styles['instalment-card-container']}
                  data-test="calculator-instalment-card-container"
                >
                  <InstalmentCard
                    onClick={onTermChange}
                    className={styles['instalment-card']}
                    monthlyPayment={termOffer?.monthlyPayment}
                    totalInterest={termOffer?.totalInterest}
                    totalRepayableAmount={termOffer?.totalRepayableAmount}
                    term={term}
                    isUnderLimit={
                      (calculatorAmount || constraintsAmountDefaultValue || 0) <
                      amountFrom
                    }
                    amountFrom={amountFrom}
                    active={
                      calculatorTerm !== undefined
                        ? calculatorTerm === term
                        : constraints.termInterval.min === term
                    }
                    isLoading={isLoadingOffer}
                  />
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
});

export default Calculator;
